import { Monitor, Moon, Sun } from "lucide-react";
import { FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { type Theme } from "~/constants";
import { useToast } from "~/hooks/use-toast";
import { useTheme } from "~/hooks/useTheme";
import { trackEvent } from "~/utils/analytics";

const themeOptions: {
  value: Theme;
  icon: typeof Sun;
  labelId: string;
  defaultLabel: string;
}[] = [
  {
    value: "light",
    icon: Sun,
    labelId: "settings.theme.light",
    defaultLabel: "Light",
  },
  {
    value: "dark",
    icon: Moon,
    labelId: "settings.theme.dark",
    defaultLabel: "Dark",
  },
  {
    value: "system",
    icon: Monitor,
    labelId: "settings.theme.system",
    defaultLabel: "System",
  },
];

export const ThemeSelector: FC = () => {
  const { theme, setTheme, mounted } = useTheme();
  const { toast } = useToast();
  const intl = useIntl();

  const handleThemeChange = (newTheme: string) => {
    setTheme(newTheme as Theme);
    trackEvent({
      eventName: "app/settings_account/theme.button/click",
      properties: { theme: newTheme },
    });
    toast({
      description: intl.formatMessage({
        id: "settings.theme.updated",
        defaultMessage: "Theme updated",
      }),
      duration: 2000,
    });
  };

  if (!mounted) {
    return (
      <div className="flex flex-col gap-2">
        <div className="h-4 w-12 bg-muted rounded animate-pulse" />
        <div className="flex gap-1 p-1 bg-border rounded-sm h-9 w-[220px] animate-pulse" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-muted-foreground">
        <FormattedMessage id="settings.theme" defaultMessage="Theme" />
        <span className="ml-1 text-xs text-muted-foreground">(Beta)</span>
      </p>
      <Tabs value={theme} onValueChange={handleThemeChange}>
        <TabsList>
          {themeOptions.map(({ value, icon: Icon, labelId, defaultLabel }) => (
            <TabsTrigger
              key={value}
              value={value}
              className="flex items-center gap-1.5 px-3"
              aria-describedby={
                value === "system" ? "system-theme-hint" : undefined
              }
            >
              <Icon className="w-3.5 h-3.5" />
              <FormattedMessage id={labelId} defaultMessage={defaultLabel} />
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <p
        id="system-theme-hint"
        className="text-xs text-muted-foreground transition-colors"
      >
        <FormattedMessage
          id="settings.theme.systemHint"
          defaultMessage="System follows your device's light/dark setting"
        />
      </p>
    </div>
  );
};
